import IterableWeakMap from '@/api/common/IterableWeakMap';
import { ItemViewModel, ListViewModel } from '@/definitions/view-models';
import { getLogger } from 'loglevel';
import { ModuleEventTypes, eventModule } from '@/api/common/eventModule';
import { getDelay } from '@/definitions/common/base';
import { throttleWithArgs } from '@/common/utils';
import { getViewModelByName } from '@/api/common';

const logger = getLogger('[auto-update-helper]');
logger.setLevel('warn');

type ListModule = ListViewModel<any, any>;
type ItemModule = ItemViewModel<any>;
type AutoUpdateModuleOptions = Record<string, never>;

export class AutoUpdateHelper {
  iterableListVMWeakMap = new IterableWeakMap([]);
  iterableItemVMWeakMap = new IterableWeakMap([]);

  constructor() {
    const handler = this.updateHandler.bind(this);
    this.throttleWithArgsUpdateHandler = throttleWithArgs(handler, 4000);
  }

  addListInstance(module: ListModule) {
    const hasModule = this.iterableListVMWeakMap.get(module);
    if (!hasModule) {
      logger.info(':add list and listVM weak map size:', module.name, this.listModules().length);
      this.iterableListVMWeakMap.set(module, true);
    }
    return module;
  }

  removeListInstance(module: ListModule | undefined) {
    logger.info(':remove list and listVM weak map size:', module?.name, this.listModules().length);
    if (module) this.iterableListVMWeakMap.delete(module);
  }

  addItemInstance(module: ItemModule) {
    const hasModule = this.iterableItemVMWeakMap.get(module);
    if (!hasModule) {
      this.iterableItemVMWeakMap.set(module, true);
      logger.info(':add item and itemVM weak map size:', this.itemModules().length);
    }
    return module;
  }

  removeItemInstance(module: ListModule | undefined) {
    logger.info(':remove item and itemVM weak map size:', module?.name, this.itemModules().length);
    if (module) this.iterableItemVMWeakMap.delete(module);
  }

  protected listModules(): ListModule[] {
    return Array.from(this.iterableListVMWeakMap.keys());
  }

  protected itemModules(): ItemModule[] {
    return Array.from(this.iterableItemVMWeakMap.keys());
  }

  async createHandler(name: string, newItem?: any, exclude: any[] = []) {
    logger.info(':create start ', name, newItem);
    const modules = this.listModules();
    for (const module of modules) {
      const isSupported = this.isSupportedListVM(module, name, exclude);
      if (isSupported) {
        logger.info(':create execute ', name, newItem, module);
        await module.autoUpdateDebounceGet();
        await module.getStatistics();
      }
    }
  }

  async throttleWithArgsUpdateHandler(name: string, newItem: any, exclude: any[] = [], options: AutoUpdateModuleOptions = {}): Promise<any> | never {
    throw new Error('not implemented');
  }

  updateListVMModule(module: ListModule, newItem: any, name: string, exclude: any[], options: AutoUpdateModuleOptions) {
    const isSupported = this.isSupportedListVM(module, name, exclude);
    const sourceItem = module.items.find((item: any) => item.id === newItem.id);
      if (isSupported && sourceItem && newItem) {
        logger.info(':update LIST execute ', name, sourceItem);
        Object.assign(sourceItem, newItem);
      }
  }

  updateItemVMModule(module: ItemModule, newItem: any, name: string, exclude: any[], options: AutoUpdateModuleOptions) {
    const isSupported = this.isSupportedItemVM(module, name, exclude);
    if (isSupported && newItem.id === module.item.id) {
      logger.info(':update ITEM execute ', name, module);
      module.setItemsState(newItem, null, true);
    }
  }

  updateHandler(name: string, newItem: any, exclude: any[] = [], options: AutoUpdateModuleOptions = {}) {
    logger.info(':update start ', name, JSON.stringify(newItem));
    const modules = this.listModules();
    const itemModules = this.itemModules();
    eventModule.current = eventModule.build(ModuleEventTypes.Update, name, newItem);

    for (const module of modules) {
      this.updateListVMModule(module, newItem, name, exclude, options);
    }

    for (const module of itemModules) {
      this.updateItemVMModule(module, newItem, name, exclude, newItem);
    }
  }

  async remoteUpdateHandler(name: string, newItem: any, exclude: any[] = [], options: AutoUpdateModuleOptions = {}) {
    const viewModel = getViewModelByName(name) as ItemViewModel<any>;
    await getDelay(100);
    await viewModel.get(newItem.id, true);
    this.updateHandler(name, viewModel.item, exclude);
  }

  async deleteHandler(name: string, id: any, exclude: any[] = []) {
    logger.info(':delete start ', name, id, this.listModules());
    const modules = this.listModules();
    for (const module of modules) {
      const isSupported = this.isSupportedListVM(module, name, exclude);
      if (isSupported) {
        logger.info(':delete execute ', name, id);
        module.items = module.items.filter((v) => v.id !== id);
        await module.autoUpdateDebounceGet();
      }
    }
  }

  isSupportedListVM(module: ListViewModel<any, any>, name: string, exclude: any[] = []) {
    return module.autoUpdateWithHelper && module.name === name && !exclude.includes(module);
  }

  isSupportedItemVM(module: ItemViewModel<any>, name: string, exclude: any[] = []) {
    return module.autoUpdateWithHelper && module.name === name && !exclude.includes(module);
  }

  updateIvmModulesByLvmModule(module: ListViewModel<any, any>) {
    for (let i = 0; i < module.items.length; i++) {
      autoUpdateHelper.updateHandler(module.name, module.items[i]);
    }
  }
}

export const autoUpdateHelper = new AutoUpdateHelper();
